import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  RegisteredStudent,
  GradeLevel,
  addStudent,
  updateStudent,
  deleteStudent,
} from '../api/families';
import { Student } from '../api/students';
import StudentStatusCard, { StudentStatusInfo } from './StudentStatusCard';

const GRADE_OPTIONS: { value: GradeLevel; label: string }[] = [
  { value: 'K', label: 'Kindergarten' },
  { value: '1', label: '1st Grade' },
  { value: '2', label: '2nd Grade' },
  { value: '3', label: '3rd Grade' },
  { value: '4', label: '4th Grade' },
  { value: '5', label: '5th Grade' },
  { value: '6', label: '6th Grade' },
  { value: '7', label: '7th Grade' },
  { value: '8', label: '8th Grade' },
];

interface RegisteredStudentsListProps {
  students: RegisteredStudent[];
  dismissalRecords: Student[]; // Current dismissal board records
  onStudentUpdated: () => void;
}

const getStudentStatus = (
  registeredStudent: RegisteredStudent,
  dismissalRecords: Student[]
): StudentStatusInfo => {
  // Find the dismissal record for this registered student
  // Match by registered_student_id field in dismissal record
  const dismissalRecord = dismissalRecords.find(
    (record) => record.registered_student_id === registeredStudent.id
  );

  if (!dismissalRecord) {
    return { status: 'not_checked_in' };
  }

  if (dismissalRecord.dismissed_at) {
    return {
      status: 'dismissed',
      checkedInAt: dismissalRecord.checked_in_at,
      dismissedAt: dismissalRecord.dismissed_at,
    };
  }

  return {
    status: 'waiting',
    checkedInAt: dismissalRecord.checked_in_at,
  };
};

const RegisteredStudentsList: React.FC<RegisteredStudentsListProps> = ({
  students,
  dismissalRecords,
  onStudentUpdated
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState<RegisteredStudent | null>(null);
  const [deletingStudent, setDeletingStudent] = useState<RegisteredStudent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    grade: '' as GradeLevel | '',
    medical_notes: '',
    pickup_type: 'car' as 'car' | 'foot',
  });

  const handleOpenAdd = () => {
    setEditingStudent(null);
    setFormData({
      first_name: '',
      last_name: '',
      grade: '',
      medical_notes: '',
      pickup_type: 'car',
    });
    setError(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (student: RegisteredStudent) => {
    setEditingStudent(student);
    setFormData({
      first_name: student.first_name,
      last_name: student.last_name,
      grade: student.grade,
      medical_notes: student.medical_notes || '',
      pickup_type: student.pickup_type,
    });
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStudent(null);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.grade) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingStudent) {
        await updateStudent(editingStudent.id, {
          ...formData,
          grade: formData.grade as GradeLevel,
        });
      } else {
        await addStudent({
          ...formData,
          grade: formData.grade as GradeLevel,
        });
      }
      onStudentUpdated();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save student');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDelete = (student: RegisteredStudent) => {
    setDeletingStudent(student);
    setError(null);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingStudent(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingStudent) return;

    setLoading(true);
    setError(null);

    try {
      await deleteStudent(deletingStudent.id);
      onStudentUpdated();
      handleCloseDeleteDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete student');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 2
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          My Students
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleOpenAdd}
          fullWidth={isMobile}
          sx={{
            minHeight: { xs: 44, sm: 36 },
            fontSize: { xs: '0.9375rem', sm: '0.875rem' }
          }}
        >
          Add Student
        </Button>
      </Box>

      {students.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            No students added yet. Click "Add Student" to get started.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: { xs: 2, sm: 2, md: 3 },
          }}
        >
          {students.map((student) => (
            <StudentStatusCard
              key={student.id}
              student={student}
              statusInfo={getStudentStatus(student, dismissalRecords)}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDelete}
            />
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {editingStudent ? 'Edit Student' : 'Add Student'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
            <TextField
              label="Last Name"
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <TextField
              label="Grade"
              select
              required
              value={formData.grade}
              onChange={(e) => setFormData({ ...formData, grade: e.target.value as GradeLevel })}
            >
              {GRADE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Pickup Type"
              select
              value={formData.pickup_type}
              onChange={(e) =>
                setFormData({ ...formData, pickup_type: e.target.value as 'car' | 'foot' })
              }
            >
              <MenuItem value="car">Car</MenuItem>
              <MenuItem value="foot">Foot</MenuItem>
            </TextField>
          </Box>
          <TextField
            label="Medical Notes (Optional)"
            fullWidth
            multiline
            rows={3}
            value={formData.medical_notes}
            onChange={(e) => setFormData({ ...formData, medical_notes: e.target.value })}
            placeholder="Allergies, medications, special needs, etc."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 }, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to remove{' '}
            <strong>
              {deletingStudent?.first_name} {deletingStudent?.last_name}
            </strong>{' '}
            from your family? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 }, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisteredStudentsList;
